export interface Question {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface Book {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  colour: string;
  bgColour: string;
  borderColour: string;
  icon: string;
  questions: Question[];
}

export const BOOKS: Book[] = [
  {
    id: 'bs7671',
    title: 'BS 7671 – IET Wiring Regulations (18th Edition)',
    shortTitle: 'BS 7671',
    description: 'The national standard for electrical installations in the UK. Covers requirements for design, erection, and verification of electrical installations.',
    colour: 'text-blue-700',
    bgColour: 'bg-blue-50',
    borderColour: 'border-blue-200',
    icon: '📘',
    questions: [
      {
        id: 'bs1',
        question: 'What is the maximum disconnection time for a 230V TN system final circuit not exceeding 32A?',
        options: ['0.1 seconds', '0.2 seconds', '0.4 seconds', '5 seconds'],
        correctIndex: 2,
        explanation: 'BS 7671 Table 41.1 specifies 0.4 seconds for 230V TN systems for circuits up to 32A.'
      },
      {
        id: 'bs2',
        question: 'Which regulation covers the requirement for RCD protection on socket outlets up to 20A in domestic premises?',
        options: ['Regulation 411.3.3', 'Regulation 415.1', 'Regulation 531.3.3', 'Regulation 314.1'],
        correctIndex: 0,
        explanation: 'Regulation 411.3.3 requires RCD protection (≤30mA) for socket outlets up to 20A in domestic premises.'
      },
      {
        id: 'bs3',
        question: 'What does "TT" earthing system mean?',
        options: [
          'Both supply and installation earth are connected to the neutral',
          'Supply earth is connected to neutral; installation has separate earth electrode',
          'Both supply and installation have separate earth electrodes',
          'No earth connection is required'
        ],
        correctIndex: 2,
        explanation: 'TT: the first T = supply source directly earthed, second T = exposed conductive parts of the installation connected to earth via a local electrode independent of the supply earth.'
      },
      {
        id: 'bs4',
        question: 'What is the minimum IP rating required for electrical equipment installed in a bathroom Zone 1?',
        options: ['IPX2', 'IPX4', 'IPX5', 'IPX7'],
        correctIndex: 1,
        explanation: 'BS 7671 Section 701 requires a minimum of IPX4 for Zone 1 in bathrooms (IPX5 where water jets are likely).'
      },
      {
        id: 'bs5',
        question: 'Under BS 7671, what colour is the line conductor in a single-phase fixed wiring installation?',
        options: ['Red', 'Black', 'Brown', 'Grey'],
        correctIndex: 2,
        explanation: 'Since the 2004 harmonisation, the line (live) conductor in single-phase fixed wiring is brown.'
      },
      {
        id: 'bs6',
        question: 'What is the purpose of a main protective bonding conductor?',
        options: [
          'To carry fault current to the consumer unit',
          'To connect extraneous conductive parts to the main earthing terminal',
          'To provide a return path for normal current',
          'To protect against overload'
        ],
        correctIndex: 1,
        explanation: 'Main protective bonding connects extraneous conductive parts (gas, water, structural steel) to the main earthing terminal to equalise potential.'
      },
      {
        id: 'bs7',
        question: 'What is the maximum Zs (earth fault loop impedance) for a 32A Type B MCB in a TN system?',
        options: ['0.35 Ω', '1.09 Ω', '1.37 Ω', '0.86 Ω'],
        correctIndex: 2,
        explanation: 'From BS 7671 Table 41.3, the maximum Zs for a 32A Type B MCB is 1.37 Ω to achieve 0.4s disconnection.'
      },
      {
        id: 'bs8',
        question: 'Which part of BS 7671 covers special installations and locations?',
        options: ['Part 4', 'Part 5', 'Part 6', 'Part 7'],
        correctIndex: 3,
        explanation: 'Part 7 of BS 7671 covers special installations and locations such as bathrooms, swimming pools, solar PV, EV charging, etc.'
      },
      {
        id: 'bs9',
        question: 'What does SELV stand for?',
        options: [
          'Safe Extra Low Voltage',
          'Separated Extra Low Voltage',
          'Standard Electrical Low Voltage',
          'Supplementary Earth Low Voltage'
        ],
        correctIndex: 0,
        explanation: 'SELV = Safety Extra Low Voltage. It is a system where the voltage cannot exceed 50V AC or 120V ripple-free DC and is separated from earth and other systems.'
      },
      {
        id: 'bs10',
        question: 'What is the minimum cross-sectional area for a main protective bonding conductor where the supply neutral is 16mm²?',
        options: ['4mm²', '6mm²', '10mm²', '16mm²'],
        correctIndex: 2,
        explanation: 'BS 7671 Table 54.8: where the supply neutral is 16mm², the main protective bonding conductor must be at least 10mm².'
      }
    ]
  },
  {
    id: 'on_site_guide',
    title: 'On-Site Guide (OSG) – IET',
    shortTitle: 'On-Site Guide',
    description: 'A practical companion to BS 7671 providing guidance on cable selection, circuit design, and installation methods for everyday electrical work.',
    colour: 'text-green-700',
    bgColour: 'bg-green-50',
    borderColour: 'border-green-200',
    icon: '📗',
    questions: [
      {
        id: 'osg1',
        question: 'When selecting a cable for a circuit, which factor is NOT directly considered in the OSG cable selection tables?',
        options: ['Ambient temperature', 'Grouping of cables', 'Cable colour', 'Installation method'],
        correctIndex: 2,
        explanation: 'Cable colour is not a factor in current-carrying capacity selection. The OSG tables consider ambient temperature, grouping, and installation method (reference methods).'
      },
      {
        id: 'osg2',
        question: 'What reference method is used for a cable clipped directly to a non-metallic surface?',
        options: ['Method A', 'Method B', 'Method C', 'Method D'],
        correctIndex: 2,
        explanation: 'Reference Method C applies to cables clipped directly to a non-metallic surface, giving higher current-carrying capacity than enclosed methods.'
      },
      {
        id: 'osg3',
        question: 'What is the standard voltage drop limit for a lighting circuit in a domestic installation?',
        options: ['1%', '3%', '5%', '10%'],
        correctIndex: 1,
        explanation: 'The OSG recommends a maximum voltage drop of 3% for lighting circuits (approximately 6.9V on a 230V supply).'
      },
      {
        id: 'osg4',
        question: 'A 2.5mm² twin and earth cable clipped to a surface (Method C) has a current rating of approximately:',
        options: ['18A', '27A', '32A', '40A'],
        correctIndex: 1,
        explanation: 'A 2.5mm² flat twin and earth cable (70°C thermoplastic) clipped to a surface has a current rating of approximately 27A.'
      },
      {
        id: 'osg5',
        question: 'What correction factor is applied when cables are grouped together?',
        options: [
          'A factor greater than 1 to increase the rating',
          'A factor less than 1 to reduce the rating',
          'No correction is needed for grouping',
          'A factor of exactly 1.0 always'
        ],
        correctIndex: 1,
        explanation: 'Grouping correction factors (Cg) are less than 1.0 because grouped cables cannot dissipate heat as effectively, reducing their current-carrying capacity.'
      },
      {
        id: 'osg6',
        question: 'What is the minimum depth cables must be buried when installed underground without mechanical protection?',
        options: ['300mm', '450mm', '600mm', '750mm'],
        correctIndex: 2,
        explanation: 'The OSG recommends cables buried underground without mechanical protection should be at a minimum depth of 600mm (0.6m).'
      },
      {
        id: 'osg7',
        question: 'For a ring final circuit, what is the maximum floor area it should serve in a domestic installation?',
        options: ['50m²', '75m²', '100m²', '150m²'],
        correctIndex: 2,
        explanation: 'A ring final circuit should not serve a floor area exceeding 100m² in a domestic installation according to the OSG.'
      },
      {
        id: 'osg8',
        question: 'What is the purpose of applying a derating factor for cables installed in thermal insulation?',
        options: [
          'To account for increased resistance at higher temperatures',
          'To prevent overheating as insulation traps heat around the cable',
          'To comply with fire regulations',
          'To reduce voltage drop'
        ],
        correctIndex: 1,
        explanation: 'Thermal insulation prevents heat dissipation from the cable. A derating factor (as low as 0.5 for fully surrounded cables) prevents dangerous overheating.'
      },
      {
        id: 'osg9',
        question: 'Which of the following is a valid installation method for a cable in a stud wall where it cannot be run in a zone?',
        options: [
          'Run at any angle across the stud',
          'Use a cable protected by earthed metal conduit',
          'Use any cable without additional protection',
          'Run diagonally to the nearest socket'
        ],
        correctIndex: 1,
        explanation: 'Where cables cannot be run in prescribed zones, they must be protected by earthed metal conduit or equivalent mechanical protection.'
      },
      {
        id: 'osg10',
        question: 'What is the maximum number of 13A socket outlets recommended on a single radial circuit protected by a 20A MCB?',
        options: ['5', '10', 'Unlimited within the floor area limit', '20'],
        correctIndex: 2,
        explanation: 'The OSG does not limit the number of socket outlets on a radial circuit — it limits the floor area served (50m² for a 20A radial, 75m² for a 32A radial).'
      }
    ]
  },
  {
    id: 'guidance_note_3',
    title: 'Guidance Note 3 – Inspection & Testing (IET)',
    shortTitle: 'Guidance Note 3',
    description: 'Covers the inspection and testing of electrical installations, including initial verification, periodic inspection, and reporting requirements.',
    colour: 'text-amber-700',
    bgColour: 'bg-amber-50',
    borderColour: 'border-amber-200',
    icon: '📙',
    questions: [
      {
        id: 'gn3_1',
        question: 'What is the correct sequence for initial verification testing?',
        options: [
          'Insulation resistance → Continuity → Polarity → Earth fault loop impedance',
          'Continuity → Insulation resistance → Polarity → Earth fault loop impedance',
          'Polarity → Continuity → Insulation resistance → Earth fault loop impedance',
          'Earth fault loop impedance → Polarity → Continuity → Insulation resistance'
        ],
        correctIndex: 1,
        explanation: 'The correct sequence is: Continuity of protective conductors → Insulation resistance → Polarity → Earth fault loop impedance → RCD testing. Dead tests before live tests.'
      },
      {
        id: 'gn3_2',
        question: 'What test voltage is used for insulation resistance testing of circuits up to 500V?',
        options: ['250V DC', '500V DC', '1000V DC', '230V AC'],
        correctIndex: 1,
        explanation: 'For circuits with a nominal voltage up to 500V, insulation resistance is tested at 500V DC. The minimum acceptable result is 1MΩ.'
      },
      {
        id: 'gn3_3',
        question: 'What is the minimum acceptable insulation resistance value for a domestic installation?',
        options: ['0.5 MΩ', '1 MΩ', '2 MΩ', '200 MΩ'],
        correctIndex: 1,
        explanation: 'BS 7671 Table 61 specifies a minimum insulation resistance of 1 MΩ for circuits up to 500V nominal.'
      },
      {
        id: 'gn3_4',
        question: 'What does a continuity test on a ring final circuit verify?',
        options: [
          'That the circuit has no earth fault',
          'That the ring is continuous and no spurs are incorrectly connected',
          'That the insulation is intact',
          'That the RCD operates within time limits'
        ],
        correctIndex: 1,
        explanation: 'The ring final circuit continuity test (r1+r2 method) verifies the ring is complete and identifies any interconnected spurs or breaks in the ring.'
      },
      {
        id: 'gn3_5',
        question: 'What is the purpose of a polarity test?',
        options: [
          'To check the earth fault loop impedance',
          'To verify that all single-pole devices are connected in the line conductor only',
          'To measure insulation resistance',
          'To test RCD operation'
        ],
        correctIndex: 1,
        explanation: 'A polarity test confirms that all single-pole protective and switching devices are connected in the line (live) conductor, not the neutral.'
      },
      {
        id: 'gn3_6',
        question: 'An Electrical Installation Certificate (EIC) is required for:',
        options: [
          'Periodic inspection only',
          'New installations and additions or alterations to existing installations',
          'Minor works only',
          'Fault finding only'
        ],
        correctIndex: 1,
        explanation: 'An EIC is required for new installations and for additions or alterations. A Minor Electrical Installation Works Certificate is used for minor works that do not include a new circuit.'
      },
      {
        id: 'gn3_7',
        question: 'What RCD test checks that the device trips within the required time at 5× the rated residual current?',
        options: ['Ramp test', '1× In test', '5× In test', 'Half-wave test'],
        correctIndex: 2,
        explanation: 'The 5× In test (5 times rated residual current) must cause the RCD to trip in ≤40ms. The 1× In test must trip in ≤300ms for a standard RCD.'
      },
      {
        id: 'gn3_8',
        question: 'What does EICR stand for?',
        options: [
          'Electrical Installation Condition Report',
          'Electrical Inspection and Compliance Record',
          'Electrical Installation Completion Report',
          'Electrical Inspection Certification Register'
        ],
        correctIndex: 0,
        explanation: 'EICR = Electrical Installation Condition Report. It is produced following a periodic inspection and testing of an existing installation.'
      },
      {
        id: 'gn3_9',
        question: 'What code is used on an EICR to indicate a deficiency that requires urgent remedial action?',
        options: ['C1', 'C2', 'C3', 'FI'],
        correctIndex: 0,
        explanation: 'C1 = Danger present, risk of injury. Immediate remedial action required. C2 = Potentially dangerous. C3 = Improvement recommended. FI = Further investigation required.'
      },
      {
        id: 'gn3_10',
        question: 'Before carrying out insulation resistance tests, what must be done with electronic equipment?',
        options: [
          'Leave it connected — it will not be affected',
          'Disconnect or short out electronic components to prevent damage',
          'Increase the test voltage to 1000V',
          'Test each circuit individually at 250V'
        ],
        correctIndex: 1,
        explanation: 'Electronic equipment (dimmers, RCDs, surge protection devices, electronic ballasts) must be disconnected or short-circuited before applying 500V DC insulation resistance test voltage to avoid damage.'
      }
    ]
  },
  {
    id: 'am2',
    title: 'AM2 Assessment – Electrotechnical Certification Scheme',
    shortTitle: 'AM2 Assessment',
    description: 'The end-point assessment for apprentice electricians. Tests practical skills, safe isolation, fault finding, and inspection and testing competencies.',
    colour: 'text-purple-700',
    bgColour: 'bg-purple-50',
    borderColour: 'border-purple-200',
    icon: '📓',
    questions: [
      {
        id: 'am2_1',
        question: 'What is the correct safe isolation procedure before working on an electrical circuit?',
        options: [
          'Switch off the MCB and begin work immediately',
          'Identify → Isolate → Lock off → Prove dead → Post warning notice',
          'Turn off the main switch and test with a screwdriver',
          'Isolate → Test → Begin work'
        ],
        correctIndex: 1,
        explanation: 'Safe isolation: Identify the circuit → Isolate at the correct point → Lock off (secure isolation) → Prove dead using an approved voltage indicator → Post warning notice. Always use a proving unit to verify the tester works.'
      },
      {
        id: 'am2_2',
        question: 'Which instrument is used to prove a circuit is dead before working on it?',
        options: [
          'A multimeter set to resistance',
          'An approved voltage indicator (GS38 compliant)',
          'A neon screwdriver',
          'A clamp meter'
        ],
        correctIndex: 1,
        explanation: 'An approved voltage indicator compliant with GS38 must be used. Neon screwdrivers are not approved for proving dead. Always test the instrument on a known live source before and after.'
      },
      {
        id: 'am2_3',
        question: 'In the AM2 practical assessment, what does the fault-finding exercise typically require?',
        options: [
          'Designing a new circuit from scratch',
          'Identifying and rectifying introduced faults in a wiring installation',
          'Installing a new consumer unit',
          'Writing a risk assessment'
        ],
        correctIndex: 1,
        explanation: 'The AM2 fault-finding exercise presents a pre-wired installation with introduced faults. Candidates must systematically identify and rectify the faults within the time allowed.'
      },
      {
        id: 'am2_4',
        question: 'What is the purpose of a "proving unit" during safe isolation?',
        options: [
          'To measure earth fault loop impedance',
          'To verify that the voltage indicator is functioning correctly before and after proving dead',
          'To test RCD operation',
          'To check insulation resistance'
        ],
        correctIndex: 1,
        explanation: 'A proving unit generates a known voltage to verify the voltage indicator works correctly. It must be used before and after proving dead to confirm the tester has not failed.'
      },
      {
        id: 'am2_5',
        question: 'During the AM2, which document must be completed to certify a new installation?',
        options: [
          'Electrical Installation Condition Report (EICR)',
          'Minor Electrical Installation Works Certificate',
          'Electrical Installation Certificate (EIC)',
          'Periodic Inspection Report'
        ],
        correctIndex: 2,
        explanation: 'An Electrical Installation Certificate (EIC) must be completed for new installations. The AM2 assessment requires candidates to complete all relevant certification documentation.'
      },
      {
        id: 'am2_6',
        question: 'What is the maximum permitted operating time for a 30mA RCD at 1× rated residual current (1× In)?',
        options: ['40ms', '150ms', '300ms', '500ms'],
        correctIndex: 2,
        explanation: 'A 30mA RCD must operate within 300ms at 1× rated residual current (30mA). At 5× rated current (150mA), it must operate within 40ms.'
      },
      {
        id: 'am2_7',
        question: 'What type of circuit is typically installed in the AM2 practical assessment?',
        options: [
          'Three-phase industrial motor circuit',
          'Domestic lighting and power circuits with consumer unit',
          'High-voltage distribution circuit',
          'Data network cabling'
        ],
        correctIndex: 1,
        explanation: 'The AM2 practical assessment involves installing domestic-type lighting and power circuits, connecting a consumer unit, and completing inspection, testing, and certification.'
      },
      {
        id: 'am2_8',
        question: 'What does PPE stand for and when must it be worn?',
        options: [
          'Personal Protective Equipment — only when working at height',
          'Personal Protective Equipment — whenever there is a risk of injury identified in the risk assessment',
          'Professional Practice Equipment — during assessments only',
          'Personal Protective Equipment — only for live working'
        ],
        correctIndex: 1,
        explanation: 'PPE = Personal Protective Equipment. It must be worn whenever a risk assessment identifies a risk of injury. In electrical work this includes insulated gloves, safety footwear, and eye protection as appropriate.'
      },
      {
        id: 'am2_9',
        question: 'What regulation requires that electrical work in dwellings must comply with Part P of the Building Regulations?',
        options: [
          'The Electricity at Work Regulations 1989',
          'The Building Regulations 2010 (England)',
          'The Health and Safety at Work Act 1974',
          'The IET Wiring Regulations'
        ],
        correctIndex: 1,
        explanation: 'Part P of the Building Regulations 2010 (England) requires that certain electrical installation work in dwellings is designed and installed to be safe and either notified to a building control body or carried out by a registered competent person.'
      },
      {
        id: 'am2_10',
        question: 'What is the purpose of supplementary bonding in a bathroom?',
        options: [
          'To provide an earth return path for the circuit',
          'To equalise potential between simultaneously accessible exposed and extraneous conductive parts',
          'To protect against overload',
          'To reduce earth fault loop impedance'
        ],
        correctIndex: 1,
        explanation: 'Supplementary bonding in bathrooms (where required) connects simultaneously accessible exposed and extraneous conductive parts to equalise potential and reduce the risk of electric shock.'
      }
    ]
  },
  {
    id: 'health_safety',
    title: 'Health & Safety – Electrical Regulations & Legislation',
    shortTitle: 'Health & Safety',
    description: 'Covers the key legislation and regulations governing electrical safety at work, including the Electricity at Work Regulations 1989, Health and Safety at Work Act 1974, and related guidance.',
    colour: 'text-red-700',
    bgColour: 'bg-red-50',
    borderColour: 'border-red-200',
    icon: '📕',
    questions: [
      {
        id: 'hs1',
        question: 'Under the Electricity at Work Regulations 1989, who has a duty to comply with the regulations?',
        options: [
          'Only employers',
          'Only employees',
          'Employers, employees, and self-employed persons',
          'Only the Health and Safety Executive (HSE)'
        ],
        correctIndex: 2,
        explanation: 'The Electricity at Work Regulations 1989 place duties on employers, employees, and self-employed persons. Regulation 3 sets out these duty holders.'
      },
      {
        id: 'hs2',
        question: 'What does Regulation 4 of the Electricity at Work Regulations 1989 require?',
        options: [
          'All electrical work must be done by qualified electricians only',
          'Electrical systems must be constructed and maintained to prevent danger',
          'All circuits must be tested annually',
          'Live working is prohibited in all circumstances'
        ],
        correctIndex: 1,
        explanation: 'Regulation 4 requires that all electrical systems are constructed and maintained so as to prevent danger, as far as reasonably practicable.'
      },
      {
        id: 'hs3',
        question: 'What is the primary purpose of the Health and Safety at Work Act 1974?',
        options: [
          'To set specific technical standards for electrical installations',
          'To provide a framework for securing the health, safety, and welfare of persons at work',
          'To regulate the training of electricians',
          'To specify maximum working hours'
        ],
        correctIndex: 1,
        explanation: 'The Health and Safety at Work Act 1974 is the primary piece of legislation covering occupational health and safety in Great Britain. It provides the framework for managing workplace health and safety.'
      },
      {
        id: 'hs4',
        question: 'Under the Management of Health and Safety at Work Regulations 1999, what must employers carry out?',
        options: [
          'Annual electrical testing',
          'A suitable and sufficient risk assessment',
          'Monthly fire drills',
          'Daily equipment inspections'
        ],
        correctIndex: 1,
        explanation: 'Regulation 3 of the Management of Health and Safety at Work Regulations 1999 requires employers to carry out a suitable and sufficient risk assessment of risks to employees and others.'
      },
      {
        id: 'hs5',
        question: 'What does COSHH stand for?',
        options: [
          'Control of Substances Hazardous to Health',
          'Code of Safe Standards for Hazardous Handling',
          'Control of Safety Standards in Hazardous Habitats',
          'Certificate of Safe Substance Handling'
        ],
        correctIndex: 0,
        explanation: 'COSHH = Control of Substances Hazardous to Health Regulations 2002. These require employers to control substances that are hazardous to health, including chemicals used in electrical work.'
      },
      {
        id: 'hs6',
        question: 'Under the Provision and Use of Work Equipment Regulations 1998 (PUWER), what must employers ensure about work equipment?',
        options: [
          'Equipment must be replaced every 5 years',
          'Equipment is suitable for its intended use, maintained, and used by trained persons',
          'All equipment must be PAT tested monthly',
          'Equipment must be purchased from approved suppliers only'
        ],
        correctIndex: 1,
        explanation: 'PUWER requires that work equipment is suitable for its intended use, maintained in a safe condition, inspected, and only used by people who have received adequate training.'
      },
      {
        id: 'hs7',
        question: 'What is the legal requirement for reporting a major injury at work?',
        options: [
          'Report to the employer within 7 days',
          'Report to the HSE under RIDDOR within 10 days',
          'Report to the local council within 24 hours',
          'No legal requirement to report'
        ],
        correctIndex: 1,
        explanation: 'Under RIDDOR (Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013), major injuries must be reported to the HSE. The responsible person must notify the HSE without delay and submit a report within 10 days.'
      },
      {
        id: 'hs8',
        question: 'What does a "permit to work" system provide?',
        options: [
          'Permission to employ additional workers',
          'A formal documented system to control hazardous work, ensuring safe conditions before work begins',
          'A licence to carry out electrical work',
          'Insurance coverage for electrical contractors'
        ],
        correctIndex: 1,
        explanation: 'A permit to work is a formal documented safety procedure that provides a written record that all foreseeable hazards have been considered and appropriate precautions taken before hazardous work begins.'
      },
      {
        id: 'hs9',
        question: 'Under the Working at Height Regulations 2005, when must a risk assessment be carried out for working at height?',
        options: [
          'Only when working above 2 metres',
          'Before any work at height is carried out',
          'Only on construction sites',
          'Only when using ladders'
        ],
        correctIndex: 1,
        explanation: 'The Working at Height Regulations 2005 require a risk assessment before any work at height. There is no minimum height threshold — even working at low heights requires assessment.'
      },
      {
        id: 'hs10',
        question: 'What is the hierarchy of control measures for managing risks?',
        options: [
          'PPE → Engineering controls → Substitution → Elimination',
          'Elimination → Substitution → Engineering controls → Administrative controls → PPE',
          'Administrative controls → PPE → Elimination → Substitution',
          'PPE → Administrative controls → Engineering controls → Elimination'
        ],
        correctIndex: 1,
        explanation: 'The hierarchy of control (most to least effective): 1. Elimination, 2. Substitution, 3. Engineering controls, 4. Administrative controls, 5. PPE. PPE is always the last resort.'
      }
    ]
  }
];
